import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
  FormGroup,
  TableSortLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

// Jogosultság ellenőrzéshez

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const VehicleList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { getAuthHeaders, hasPermission } = useAuth();
  
  // Lekérdezéshez: canManageVehicles; feltöltés/szerkesztés/törléshez: canUploadVehicles
  const canEdit = hasPermission('canUploadVehicles');

  // Szerkesztő dialógus állapotok
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({ plateNumber: '', status: 'active' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('asc');

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/Vehicles', { headers: getAuthHeaders() });
      setVehicles(response.data);
      setError(null);
    } catch (err) {
      setError('Nem sikerült betölteni a járműveket. Kérjük, próbálja újra később.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const lower = file.name.toLowerCase();
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv'))
    {
      setSelectedFile(file);
      return;
    }

    setSelectedFile(null);
    setSnackbar({
      open: true,
      severity: 'error',
      message: 'Csak .xlsx, .xls vagy .csv fájl tölthető fel.'
    });
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    console.log('Fájl feltöltése:', selectedFile.name, selectedFile.size, 'byte');

    try {
      setUploading(true);
      console.log('API hívás kezdése: /api/Vehicles/import');
      const response = await axios.post('/api/Vehicles/import', formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      });
      console.log('API válasz:', response.data);

      setSnackbar({
        open: true,
        severity: 'success',
        message: response.data?.message || 'Importálás sikeres.'
      });
      setImportDialogOpen(false);
      setSelectedFile(null);
      await loadVehicles();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Ismeretlen hiba';
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Hiba az importálás során: ' + msg
      });
    } finally {
      setUploading(false);
    }
  };

  // Szerkesztő dialógus kezelés
  const handleOpenEditDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        plateNumber: vehicle.plateNumber || '',
        status: vehicle.status || 'active'
      });
    } else {
      setEditingVehicle(null);
      setFormData({ plateNumber: '', status: 'active' });
    }
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingVehicle(null);
    setFormData({ plateNumber: '', status: 'active' });
  };

  const handleSaveVehicle = async () => {
    if (!formData.plateNumber.trim()) {
      setSnackbar({ open: true, severity: 'error', message: 'A rendszám kötelező' });
      return;
    }

    try {
      const payload = {
        plateNumber: formData.plateNumber,
        status: formData.status
      };

      if (editingVehicle) {
        await axios.put(`/api/Vehicles/${editingVehicle.id}`, payload, {
          headers: getAuthHeaders()
        });
        setSnackbar({ open: true, severity: 'success', message: 'Jármű sikeresen frissítve' });
      } else {
        await axios.post('/api/Vehicles', payload, {
          headers: getAuthHeaders()
        });
        setSnackbar({ open: true, severity: 'success', message: 'Jármű sikeresen létrehozva' });
      }
      handleCloseEditDialog();
      loadVehicles();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Hiba a jármű mentése során: ' + (err.response?.data?.message || err.message)
      });
    }
  };

  const handleDeleteVehicle = async (id) => {
    if (!window.confirm('Biztosan törölni (deaktiválni) szeretnéd ezt a járművet?')) {
      return;
    }

    try {
      await axios.delete(`/api/Vehicles/${id}`, {
        headers: getAuthHeaders()
      });
      setSnackbar({ open: true, severity: 'success', message: 'Jármű sikeresen deaktiválva' });
      loadVehicles();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Hiba a jármű törlése során: ' + (err.response?.data?.message || err.message)
      });
    }
  };

  // Szűrt járművek
  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = vehicle.plateNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = showInactive || vehicle.status === 'active';
    return matchesSearch && matchesStatus;
  });

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedVehicles = [...filteredVehicles].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusLabel = (status) => {
    return status === 'active' ? 'Aktív' : 'Inaktív';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Járművek
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEditDialog()}
            >
              Új jármű
            </Button>
          )}
          {canEdit && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<CloudUploadIcon />}
              onClick={() => setImportDialogOpen(true)}
            >
              Importálás
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadVehicles}
          >
            Frissítés
          </Button>
        </Box>
      </Box>

      {/* Szűrő panel */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Keresés"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <Button
            variant={showInactive ? 'contained' : 'outlined'}
            color={showInactive ? 'warning' : 'inherit'}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? 'Inaktívak mutatása' : 'Csak aktívak'}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {sortedVehicles.length} jármű
          </Typography>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'plateNumber'}
                    direction={orderBy === 'plateNumber' ? order : 'asc'}
                    onClick={() => handleRequestSort('plateNumber')}
                  >
                    Rendszám
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Státusz
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdAt')}
                  >
                    Létrehozva
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Műveletek</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      Nincs megjeleníthető jármű
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedVehicles.map((v) => (
                  <TableRow key={v.id} hover sx={{ opacity: v.status === 'active' ? 1 : 0.5 }}>
                    <TableCell>{v.id}</TableCell>
                    <TableCell>{v.plateNumber}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(v.status)} 
                        color={v.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{v.createdAt ? new Date(v.createdAt).toLocaleString() : ''}</TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <>
                          <Tooltip title="Szerkesztés">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenEditDialog(v)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Törlés">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteVehicle(v.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={importDialogOpen} onClose={() => !uploading && setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rendszámok importálása</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" gutterBottom>
              Válassz ki egy Excel vagy CSV fájlt (.xlsx, .xls, .csv) a rendszámok importálásához.
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              A fájl első oszlopában (vagy a "rendszám/plate" fejlécű oszlopban) legyenek a rendszámok.
            </Typography>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
              >
                Fájl kiválasztása
                <VisuallyHiddenInput type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
              </Button>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Kiválasztott fájl: {selectedFile.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={uploading}>Mégsem</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            color="primary"
            disabled={!selectedFile || uploading}
            startIcon={uploading && <CircularProgress size={20} color="inherit" />}
          >
            {uploading ? 'Importálás...' : 'Importálás'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Szerkesztő Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingVehicle ? 'Jármű szerkesztése' : 'Új jármű'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Rendszám"
              value={formData.plateNumber}
              onChange={(e) => setFormData({ ...formData, plateNumber: e.target.value })}
              fullWidth
              required
            />
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })}
                    color="primary"
                  />
                }
                label={formData.status === 'active' ? "Aktív" : "Inaktív"}
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Mégse</Button>
          <Button onClick={handleSaveVehicle} variant="contained" color="primary">
            Mentés
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VehicleList;
